import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { environment } from '../../environments/environment';
import { User } from '../models/user';
import { Observable, of } from 'rxjs';
import { LanguageService } from './language.service';
import { TranslateService } from '@ngx-translate/core';
import { KeycloakAuthService } from './keycloak-auth.service';

/**
 * This service is all about user authentication and to get logged in user information
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
   /**
    * User model
    */
   user;

   /**
    * Auth-service constructor
    * @param http
    * @param router
    * @param store
    */
   constructor(
      private http: HttpClient,
      private router: Router,
      private store: Store,
      private languageService: LanguageService,
      public translate: TranslateService,
      private keycloakAuthService: KeycloakAuthService
   ) {}

   /**
    * Method is used to set user token on local storage
    * @param userData
    */
   public signIn(userData: User): void {
      localStorage.setItem('ACCESS_TOKEN', JSON.parse(userData.token));
   }

   /**
    * Method is used to check if user is logged in or not
    */
   public isLoggedIn(): boolean {
      const token = this.getUserToken();
      const hasValidToken = token !== null && token !== undefined && token !== '';
      return hasValidToken;
   }

   /**
    * Method is used to get current user token
    */
   public getUserToken() {
      try {
         const tokenString = localStorage.getItem('ACCESS_TOKEN');

         if (!tokenString || tokenString === 'undefined' || tokenString === 'null') {
            // Don't log errors during MFA verification
            if (!this.isInMfaVerification()) {
               console.log('No valid token found in localStorage');
            }
            return null;
         }

        //  console.log('tokenString',tokenString);
         const parsedToken = JSON.parse(tokenString);
         return parsedToken;
      } catch (error) {
         // Don't log errors during MFA verification
         if (!this.isInMfaVerification()) {
            console.error('Error parsing token from localStorage:', error);
         }
         // Clear invalid token to prevent repeated errors
         localStorage.removeItem('ACCESS_TOKEN');
         return null;
      }
   }

   /**
    * Check if we're currently in MFA verification mode
    */
   private isInMfaVerification(): boolean {
      return this.router.url === '/mfa-verification' ||
             window.location.pathname === '/mfa-verification' ||
             localStorage.getItem('temp_mfa_data') !== null;
   }

   /**
    * Method is used to get current user role
    */
   public isLoggedInRole() {
      const role = localStorage.getItem('role');
      return (role && role !== 'undefined' && role !== 'null') ? role : null;
   }

   /**
    * Method is used to logOut the user and delete items from local storage
    */
   public logout(): void {
      localStorage.removeItem('ACCESS_TOKEN');
      localStorage.removeItem('role');
      localStorage.removeItem('user');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('__app_storage__');
      localStorage.removeItem('modal');
      localStorage.removeItem('websiteLanguage');
      localStorage.removeItem('aId');

      this.translate.use('en');

      const keycloak = this.keycloakAuthService.getKeycloakInstance();
      const idToken = keycloak.idToken;
      if (idToken) {
         const postLogoutRedirectUri = `${environment.currentUrl}login`
         const encodedRedirectUri = postLogoutRedirectUri
         const logoutUrl = `${keycloak.authServerUrl}/realms/${keycloak.realm}/protocol/openid-connect/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${encodedRedirectUri}`;
         console.log('logoutUrl',logoutUrl);
         window.location.href = logoutUrl;
      } else {
         console.error('Keycloak instance not available');
         this.router.navigate(['/login']);
      }
   }

   /**
    * Method is used to get current user information
    */
   public getCurrentUserObservable() {
      return this.http.get<any>(`${environment.apiUrl}/user`).pipe();
   }
   /**
    * Method used to get current user from local storage
    */
   public getCurrentUser() {
      try {
         const userString = localStorage.getItem('user');

         if (!userString || userString === 'undefined' || userString === 'null') {
            return null;
         }

         return <any>JSON.parse(userString);
      } catch (error) {
         // Don't log errors during MFA verification
         if (!this.isInMfaVerification()) {
            console.error('Error parsing user from localStorage:', error);
         }
         // Clear invalid user data to prevent repeated errors
         localStorage.removeItem('user');
         return null;
      }
   }

   /**
    * Method is used to logIn the user, calls the login api and handles user login.
    * @param email
    * @param password
    */
   login(email: string, password: string, isIdpLogin: boolean = false, institutionName?: string, role?: string, fullName?: string) {
      return this.http
         .post<any>(`${environment.apiUrl}/login`, { email, password, isIdpLogin, institutionName, role, fullName })
         .pipe(
            map((data) => {
               // store user details and jwt token in local storage to keep user logged in between page refreshes
               localStorage.setItem('ACCESS_TOKEN', JSON.stringify(data.token));
               localStorage.setItem('role', JSON.stringify(data.roleId));
               localStorage.setItem('websiteLanguage', data.language);
               localStorage.setItem('user', JSON.stringify(data));
               localStorage.setItem('modal', 'false');
               this.languageService.setSelectedLanguage(data.language);
               return data;
            })
         );
   }
   /**
    * Method used to call googleAuth api to login or sign up (if the user isn't registered) with google
    * @param name
    * @param email
    */
   googleAuth(name: string, email: string) {
      return this.http
         .post<any>(`${environment.apiUrl}/googleAuth`, {
            name,
            email,
         })
         .pipe(
            map((data) => {
               // store user details and jwt token in local storage to keep user logged in between page refreshes
               localStorage.setItem(
                  'ACCESS_TOKEN',
                  JSON.stringify(data.jwtToken)
               );
               localStorage.setItem('role', JSON.stringify(data.roleId));
               localStorage.setItem('user', JSON.stringify(data));
               localStorage.setItem('modal', 'false');
               return data;
            })
         );
   }
   /**
    *  Method used to call linkedInAuth api to login or sign up (if the user isn't registered) with linkedIn
    * @param authCode
    */
   linkedInLogin(authCode: string): any {
      return this.http
         .post<any>(`${environment.apiUrl}/linkedInAuth`, {
            authCode,
         })
         .pipe(
            map((data) => {
               localStorage.setItem(
                  'ACCESS_TOKEN',
                  JSON.stringify(data.jwtToken)
               );
               localStorage.setItem('role', JSON.stringify(data.roleId));
               localStorage.setItem('user', JSON.stringify(data));
               localStorage.setItem('modal', 'false');
               return data;
            })
         );
   }

   LTIlogin({
      token,
      user,
      assignment,
      assignmentId,
      courseId,
   }: {
      token: string;
      user: User;
      assignment?: any;
      assignmentId?: string;
      courseId?: string;
   }): any {
      localStorage.setItem('ACCESS_TOKEN', JSON.stringify(token));
      localStorage.setItem('role', user?.roleId.toString());
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('websiteLanguage', user.language);
      localStorage.setItem('LTIAssignment', JSON.stringify(assignment));
      localStorage.setItem('LTICourseId', courseId);
      localStorage.setItem('LTIAssignmentId', assignmentId);
      this.languageService.setSelectedLanguage(user.language);
      return of(user);
   }
   /**
    * Method used to call forgot password to api
    * @param email
    */
   forgotPassword(email: string) {
      return this.http
         .post<any>(`${environment.apiUrl}/forget-password`, {
            email,
         })
         .pipe();
   }

   activateAccount(email: string) {
      return this.http
         .post<any>(`${environment.apiUrl}/lti/activate`, {
            email,
         })
         .pipe();
   }

   getInstitutionDetails(institutionName: string): Observable<any> {
      return this.http.get(`${environment.apiUrl}/institutionIdP/${institutionName}`);
   }

   /**
    * Creates an external login session using a JWT token
    * @param token The JWT token from external system
    * @returns Observable with session information
    */
   createExternalLoginSession(token: string): Observable<any> {
      return this.http.post<any>(`${environment.apiUrl}/external-sessions`, {
         forLogin: true,
         externalToken: token,
         expirationSeconds: 300 // 5 minutes
      });
   }

   /**
    * Verifies an external login session
    * @param sessionId The session ID to verify
    * @returns Observable with verification result
    */
   verifyExternalLoginSession(sessionId: string): Observable<any> {
      return this.http.post<any>(`${environment.apiUrl}/external-sessions/verify`, {
         sessionId
      });
   }
}
